import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Everything is stored canonically in man-hours. This layer only converts for display/entry.
export const LABOR_UNITS = {
  hours: { factor: 1, suffix: "MH", label: "Man-hours", dp: 1 },
  minutes: { factor: 60, suffix: "man-min", label: "Man-minutes", dp: 0 },
  days: { factor: 1 / 8, suffix: "man-days", label: "Man-days (8h)", dp: 2 },
};

const LaborUnitContext = createContext(null);
export const useLaborUnit = () => useContext(LaborUnitContext) || _fallback;

const _fallback = (() => {
  const cfg = LABOR_UNITS.hours;
  return {
    unit: "hours", setUnit: () => {}, units: LABOR_UNITS, suffix: cfg.suffix, label: cfg.label,
    toDisplay: (h) => h || 0, fromDisplay: (v) => Number(v) || 0,
    fmt: (h) => `${Number(h || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })} MH`,
  };
})();

export function LaborUnitProvider({ children }) {
  const { user } = useAuth() || {};
  const [unit, setUnitState] = useState(() => {
    try { return localStorage.getItem("gf_labor_unit") || "hours"; } catch { return "hours"; }
  });

  // Adopt the user's saved preference if the browser hasn't set one yet.
  useEffect(() => {
    let hasLocal = false;
    try { hasLocal = !!localStorage.getItem("gf_labor_unit"); } catch {}
    if (!hasLocal && user?.labor_unit && LABOR_UNITS[user.labor_unit]) setUnitState(user.labor_unit);
  }, [user]);

  const setUnit = useCallback((u) => {
    if (!LABOR_UNITS[u]) return;
    setUnitState(u);
    try { localStorage.setItem("gf_labor_unit", u); } catch {}
    api.post("/auth/labor-unit", { unit: u }).catch(() => {});
  }, []);

  const cfg = LABOR_UNITS[unit] || LABOR_UNITS.hours;
  const toDisplay = useCallback((hours) => (hours == null ? 0 : hours * cfg.factor), [cfg.factor]);
  const fromDisplay = useCallback((val) => (val === "" || val == null ? 0 : Number(val) / cfg.factor), [cfg.factor]);
  const fmt = useCallback((hours, opts = {}) => {
    const s = Number(toDisplay(hours)).toLocaleString("en-US", { maximumFractionDigits: cfg.dp });
    return opts.suffix === false ? s : `${s} ${cfg.suffix}`;
  }, [cfg.dp, cfg.suffix, toDisplay]);

  return (
    <LaborUnitContext.Provider value={{ unit, setUnit, units: LABOR_UNITS, suffix: cfg.suffix, label: cfg.label, toDisplay, fromDisplay, fmt }}>
      {children}
    </LaborUnitContext.Provider>
  );
}

// Inline labor-time input: enter in man-HOURS or man-MINUTES; always stores canonical HOURS.
// Replaces the old global top-right converter with per-field flexibility (Procore-style).
export function LaborHoursInput({ value, onChange, disabled, testid, placeholder = "0", className = "", defaultUnit = "hr" }) {
  const [u, setU] = useState(defaultUnit === "min" ? "min" : "hr");
  const toDisp = (h) => (h === "" || h == null ? "" : (u === "min" ? Math.round(Number(h) * 60 * 100) / 100 : Number(h)));
  const [raw, setRaw] = useState(toDisp(value));
  useEffect(() => { setRaw(toDisp(value)); }, [value, u]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = (v) => {
    if (v === "" || v == null) { onChange(""); return; }
    const n = Number(v);
    if (Number.isNaN(n)) { onChange(""); return; }
    onChange(u === "min" ? Math.round((n / 60) * 1e6) / 1e6 : n);
  };
  return (
    <div className="flex items-center gap-1">
      <input type="number" step="any" disabled={disabled} value={raw}
        onChange={(e) => { setRaw(e.target.value); commit(e.target.value); }}
        data-testid={testid} placeholder={placeholder}
        className={`flex-1 min-w-0 ${className}`} />
      <select disabled={disabled} value={u} onChange={(e) => setU(e.target.value)}
        data-testid={testid ? `${testid}-unit` : undefined}
        className="h-full text-[10px] border border-slate-200 rounded px-1 py-1 bg-white text-slate-600 shrink-0">
        <option value="hr">hr</option>
        <option value="min">min</option>
      </select>
    </div>
  );
}
